import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowUturnLeftIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { patchBooklet } from "../../services/bookletService";
import { getApiErrorCode } from "../../utils/apiError";
import { Button } from "../../components/forms/Buttons/Button";
import { cardClass, focusRing, linkButtonClass } from "../Exams/TestBuilder/ui";
import {
  bookletErrorMessage,
  ConflictNote,
  describedBy,
  fieldClass,
  FieldError,
  invalidClass,
  linkTapClass,
  sectionTitleClass,
  tapClass,
  useFocusAfterRender,
} from "./bts";
import { LABEL_LIMIT, labelProblem, PLACEHOLDERS } from "./labelCheck";

const STALE = ["booklet_changed", "booklet_active", "exam_archived"];
const TOGGLE_ID = "bts-labels-toggle";
const SAVE_ID = "bts-labels-save";
// Braces in the texts come as values: i18next reads {{…}} written in a text as its own.
const BRACES = { open: "{{", close: "}}" };
const braced = (name) => `{${name}}`;

const valuesOf = (booklet) => ({ ...(booklet.default_labels || {}), ...(booklet.labels || {}) });
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

/**
 * The phrases the booklet prints (`labels`), each against its default in the booklet's language
 * (`default_labels`), with the placeholders it must keep. A draft changes them (PATCH print_labels: the
 * phrases that differ from the defaults); otherwise read-only. `onDirtyChange("labels", unsaved)` tells the
 * page whether there are unsaved phrases.
 */
export default function BookletLabels({ booklet, onSaved, onStale, onDirtyChange }) {
  const [t] = useTranslation();
  const draft = booklet.status === "draft";
  const defaults = booklet.default_labels || {};
  const keys = Object.keys(defaults);

  const [open, setOpen] = useState(false);
  const [base, setBase] = useState(() => valuesOf(booklet));
  const [values, setValues] = useState(base);
  const baseRef = useRef(base);
  // The phrases the user changed that someone else saved meanwhile too (their value shows under the phrase).
  const [conflicts, setConflicts] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const focusAfterRender = useFocusAfterRender();

  // The booklet read again: in a draft, the phrases the user changed keep their input, the others show the new
  // values; a booklet that is no longer a draft shows its own phrases (they cannot change).
  useEffect(() => {
    const next = valuesOf(booklet);
    const before = baseRef.current;
    const kept = (key) => booklet.status === "draft" && values[key] !== before[key];
    setValues(Object.fromEntries(Object.keys(next).map((key) => [key, kept(key) ? values[key] : next[key]])));
    setConflicts((prev) => Object.keys(next).filter((key) => kept(key) && (prev.includes(key) || next[key] !== before[key])));
    if (booklet.status !== "draft") setErrors({});
    baseRef.current = next;
    setBase(next);
  }, [booklet]); // eslint-disable-line react-hooks/exhaustive-deps

  const changedCount = keys.filter((key) => (base[key] || "").trim() !== defaults[key]).length;
  const dirty = !same(values, base);
  const unsaved = draft && dirty;
  useEffect(() => {
    onDirtyChange?.("labels", unsaved);
  }, [unsaved, onDirtyChange]);

  const set = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
    if (formError) setFormError("");
  };
  // The phrase someone else saved, instead of the user's.
  const take = (key) => {
    set(key, base[key]);
    setConflicts((prev) => prev.filter((k) => k !== key));
    document.getElementById(`bts-label-${key}`)?.focus();
  };

  // "{code}", "{first} and {last}".
  const placeholderList = (names) => {
    const items = names.map(braced);
    return items.length > 1 ? t("bts.labels.and", { list: items.slice(0, -1).join(", "), last: items[items.length - 1] }) : items[0];
  };
  // The rule of a phrase the server refused (its reason is Russian only), with the placeholders it keeps.
  const refusedText = (key) => {
    const names = PLACEHOLDERS[key];
    return names ? t("bts.labels.refused", { placeholders: placeholderList(names), count: names.length, ...BRACES }) : t("bts.labels.invalid");
  };

  // The phrases as the server checks them (labelCheck), each with what is wrong in it.
  const validate = () => {
    const found = {};
    keys.forEach((key) => {
      const result = labelProblem(key, (values[key] || "").trim());
      if (!result) return;
      if (result.problem === "empty") found[key] = t("bts.labels.empty");
      else if (result.problem === "too_long") found[key] = t("bts.labels.too_long", { count: LABEL_LIMIT });
      else if (result.problem === "missing") {
        found[key] = t("bts.labels.missing", { placeholders: placeholderList(result.missing), count: result.missing.length });
      } else {
        found[key] = t("bts.labels.braces", { placeholders: placeholderList(result.placeholders), count: result.placeholders.length, ...BRACES });
      }
    });
    return found;
  };

  const save = async (event) => {
    event.preventDefault();
    if (!dirty || saving) return;
    const found = validate();
    setErrors(found);
    setFormError("");
    const first = keys.find((key) => found[key]);
    if (first) {
      document.getElementById(`bts-label-${first}`)?.focus();
      return;
    }
    const printLabels = {};
    keys.forEach((key) => {
      const value = (values[key] || "").trim();
      if (value !== defaults[key]) printLabels[key] = value;
    });
    setSaving(true);
    try {
      const data = await patchBooklet(booklet.uuid, { print_labels: printLabels, revision: booklet.revision });
      baseRef.current = valuesOf(data);
      setValues(valuesOf(data));
      setConflicts([]);
      onSaved(data);
      toast.success(t("bts.labels.saved"));
      // Save and Cancel go: the focus goes to the card's heading.
      focusAfterRender(TOGGLE_ID);
    } catch (err) {
      const code = getApiErrorCode(err);
      const fieldErrors = err?.response?.status === 400 ? err.response.data?.print_labels : null;
      if (STALE.includes(code)) {
        onStale(err);
        focusAfterRender(TOGGLE_ID);
      } else if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
        const mapped = Object.fromEntries(Object.keys(fieldErrors).filter((key) => keys.includes(key)).map((key) => [key, refusedText(key)]));
        setErrors(mapped);
        const firstKey = keys.find((key) => mapped[key]);
        if (firstKey) focusAfterRender(`bts-label-${firstKey}`);
        else {
          setFormError(t("bts.labels.invalid"));
          focusAfterRender(SAVE_ID, TOGGLE_ID);
        }
      } else {
        setFormError(bookletErrorMessage(t, err, "bts.labels.failed"));
        focusAfterRender(SAVE_ID, TOGGLE_ID);
      }
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setValues(base);
    setErrors({});
    setFormError("");
    setConflicts([]);
    document.getElementById(TOGGLE_ID)?.focus();
  };

  return (
    <section aria-labelledby="bts-labels-title" className={`${cardClass} p-4 sm:p-5`}>
      <h3 id="bts-labels-title" className={sectionTitleClass}>
        <button
          id={TOGGLE_ID}
          type="button"
          aria-expanded={open}
          aria-controls="bts-labels-body"
          onClick={() => setOpen((value) => !value)}
          className={`-m-1 flex w-[calc(100%+0.5rem)] items-center justify-between gap-3 rounded-lg p-1 text-left max-sm:min-h-[2.75rem] ${focusRing}`}
        >
          <span>
            {t("bts.labels.title")}
            <span className="mt-0.5 block text-sm font-normal text-slate-500">
              {changedCount ? t("bts.labels.changed_count", { count: changedCount }) : t("bts.labels.all_default")}
              {unsaved && ` · ${t("bts.labels.unsaved")}`}
            </span>
          </span>
          <ChevronDownIcon className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200 ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </h3>
      {open && (
        <form id="bts-labels-body" onSubmit={save} noValidate className="mt-4 space-y-4">
          <p className="text-sm text-slate-500">{draft ? t("bts.labels.intro") : t("bts.labels.read_only")}</p>
          {keys.map((key) => {
            const value = values[key] ?? "";
            const isDefault = value.trim() === defaults[key];
            const placeholders = PLACEHOLDERS[key] || [];
            const id = `bts-label-${key}`;
            const conflict = conflicts.includes(key) && draft && value !== base[key];
            return (
              <div key={key}>
                <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                  <label htmlFor={id} className="text-sm font-medium text-slate-700">
                    {t(`bts.labels.keys.${key}`, { defaultValue: key })}
                  </label>
                  {draft && !isDefault && (
                    <button type="button" onClick={() => set(key, defaults[key])} className={`${linkButtonClass} ${linkTapClass} text-xs`}>
                      <ArrowUturnLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                      {t("bts.labels.reset")}
                    </button>
                  )}
                </div>
                <input
                  id={id}
                  type="text"
                  value={value}
                  maxLength={LABEL_LIMIT + 50}
                  disabled={!draft}
                  // Read-only, not disabled, while saving: a refused phrase takes the focus.
                  readOnly={saving}
                  onChange={(e) => set(key, e.target.value)}
                  aria-invalid={!!errors[key]}
                  aria-describedby={describedBy(`${id}-hint`, errors[key] && `${id}-error`, conflict && `${id}-conflict`)}
                  className={`${fieldClass} h-10 max-sm:h-11 ${errors[key] ? invalidClass : ""}`}
                />
                <p id={`${id}-hint`} className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                  {placeholders.length > 0 && (
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {t("bts.labels.keep")}
                      {placeholders.map((name) => (
                        <code key={name} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[11px] text-slate-700">
                          {braced(name)}
                        </code>
                      ))}
                    </span>
                  )}
                  {!isDefault && <span className="min-w-0 break-words">{t("bts.labels.default_is", { value: defaults[key] })}</span>}
                </p>
                <FieldError id={`${id}-error`}>{errors[key]}</FieldError>
                {conflict && <ConflictNote id={`${id}-conflict`} value={`«${base[key]}»`} onTake={() => take(key)} disabled={saving} />}
              </div>
            );
          })}
          {formError && (
            <p role="alert" className="text-sm text-red-700">
              {formError}
            </p>
          )}
          {/* Only while something is to be saved: no pale disabled Save beside the page's one main button. */}
          {draft && dirty && (
            <div className="flex flex-col-reverse gap-2 border-t border-slate-100 pt-4 sm:flex-row sm:justify-end">
              <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={cancel} disabled={saving} className={tapClass} />
              <Button id={SAVE_ID} type="submit" color="primary" text={t("buttons.save")} loading={saving} className={tapClass} />
            </div>
          )}
        </form>
      )}
    </section>
  );
}
